"use client";

import React from "react";
import Link from "next/link";
import { useTheme } from "next-themes";
import { Button } from "../components/ui/button";
import { Heart, Sun, Moon, ArrowLeft } from "lucide-react";
import SubdomainRedirect from "./SubdomainRedirect";

const ThemeToggle: React.FC<{
  theme: string | undefined;
  toggleTheme: () => void;
}> = React.memo(({ theme, toggleTheme }) => (
  <Button onClick={toggleTheme} variant="outline" size="sm">
    {theme === "dark" ? (
      <Sun className="mr-2 h-4 w-4" />
    ) : (
      <Moon className="mr-2 h-4 w-4" />
    )}
    {theme === "dark" ? "Light" : "Dark"}
  </Button>
));
ThemeToggle.displayName = "ThemeToggle";
const footerLinks = [
  { href: "https://about?.endure?.health", label: "About" },
  { href: "https://help?.endure?.health", label: "Help" },
  { href: "https://api?.endure?.health", label: "API" },
  { href: "https://privacy?.endure?.health", label: "Privacy" },
  { href: "https://terms?.endure?.health", label: "Terms" },
  { href: "https://contact?.endure?.health", label: "Contact" },
  { href: "https://language?.endure?.health", label: "English" },
];

const SubdomainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = React.useState<string | undefined>("light");
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  if (!mounted) return null;

  return (
    <div className="min-h-screen bg-background">
      <SubdomainRedirect />
      <header className="border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-primary" />
            <span className="text-xl font-bold text-primary">Endure</span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { if (window?.location) { window.location.href = 'https://endure.health'; } }}
              className="flex items-center"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Endure
            </Button>
            <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8">
        {children}
      </main>
      <footer className="border-t border-border mt-12 py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center mb-4">
            {footerLinks?.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2025 Endure</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SubdomainLayout;